import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import FooterLinks from '../components/FooterLinks';

const heroStyles = {
  backgroundImage:
    "linear-gradient(0deg, rgba(10, 20, 28, 0.2), rgba(10, 20, 28, 0.2)), url('/assets/images/join%20our%20team.jpeg')",
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const roles = [
  {
    title: 'Care Manager',
    intro:
      'As our Care Manager, you will play a vital leadership role in ensuring Testa Care delivers safe, effective, and person-centred services every day.',
    responsibilities: [
      'Overseeing the day-to-day operations of the service',
      'Ensuring compliance with CQC standards and internal policies',
      'Leading, supporting, and developing the care team',
      'Working closely with service users, families, and stakeholders',
      'Managing care plans, audits, and quality assurance',
      'Contributing to business growth and service development',
    ],
    requirements: [
      'Strong knowledge of CQC regulations',
      'Proven leadership in domiciliary care or health & social care',
      'Excellent communication and people skills',
      'Ability to motivate a team and drive high standards',
      'Level 5 (or working towards) in Health & Social Care',
    ],
    closing: "If you're ready to lead with passion and drive excellence, join us at Testa Care.",
  },
  {
    title: 'Care Coordinator',
    intro:
      'Our Care Coordinator is the operational heartbeat of Testa Care. This role ensures smooth scheduling, effective communication, and outstanding support for carers and service users.',
    responsibilities: [
      'Creating and managing staff rotas',
      'Allocating care visits according to service user needs',
      'Handling calls, enquiries, and daily operational tasks',
      'Maintaining accurate records and updating care plans',
      'Supporting carers with real-time assistance and problem solving',
      'Working closely with the Care Manager to ensure service continuity',
    ],
    requirements: [
      'Experience in rota management or care coordination',
      'Strong organisation and time-management skills',
      'Confident communicator with excellent attention to detail',
      'Ability to work under pressure and multi-task',
      'A caring, helpful attitude and commitment to quality care',
    ],
  },
  {
    title: 'Office Team',
    intro:
      'Our Office Team is essential in keeping the organisation running smoothly. We welcome applicants with skills across administration, finance, HR, compliance, and customer service.',
    responsibilities: [
      'Supporting daily office operations',
      'Handling enquiries from service users and families',
      'Assisting with recruitment, onboarding, and staff compliance',
      'Maintaining accurate records and documentation',
      'Supporting finance tasks such as invoicing and payroll',
      'Coordinating meetings, training, and communications',
    ],
    requirements: [
      'Strong administrative and IT skills',
      'Friendly, professional communication',
      'Ability to work well as part of a supportive team',
      'Attention to detail and a proactive attitude',
      'Experience in health & social care admin is a bonus',
    ],
  },
  {
    title: 'Carers / Support Workers',
    intro:
      'Join Testa Care as a Carer/Support Worker and make a meaningful difference every day. Whether you are experienced or new to care, we provide full training and ongoing support.',
    responsibilities: [
      'Supporting individuals with personal care, daily tasks, and wellbeing',
      'Assisting with meal preparation, medication, and household duties',
      'Promoting independence, safety, and dignity',
      'Offering companionship and emotional support',
      'Recording accurate notes after each visit',
      'Working flexibly to meet service user needs',
    ],
    requirements: [
      'A caring, patient, and compassionate nature',
      'Reliability and commitment',
      'Good communication skills',
      'Ability to work independently and as part of a team',
      'Driving licence preferred but not essential',
      'Experience is welcome but not required – full training provided',
    ],
  },
];

const JoinOurTeam = () => {
  return (
    <div className="JoinOurTeam bg-[#fdfaf6] text-[#333]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <Header />
      <div className="mt-32 sm:mt-36 lg:mt-32">
        {/* Hero */}
        <section
          className="min-h-[50vh] sm:min-h-[60vh] lg:min-h-[70vh] flex items-center justify-center text-center text-white"
          style={heroStyles}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20" />
        </section>
      <div className="h-1 w-full bg-[#2f4c74]"></div>

      <section className="bg-white py-10 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-left text-[#2b3d57] space-y-4 sm:space-y-5">
          <p className="text-sm sm:text-base md:text-lg font-semibold">Current opportunities.</p>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-light text-[#2b3d57]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            Join Our Team at Testa Care
          </h1>
          <p className="text-sm sm:text-base md:text-lg">
            We are a dedicated, close-knit team united by a shared commitment to supporting and nurturing some of the
            most vulnerable children and young people.
          </p>
          <p className="text-sm sm:text-base md:text-lg">
            At Testa Care, every team member is driven by a common purpose: to help the children in our care grow,
            thrive, and achieve their potential within a safe, stable, and supportive environment, regardless of their
            past experiences or future challenges.
          </p>
          <p className="text-sm sm:text-base md:text-lg">
            Whether you thrive leading from the front, ensuring smooth operations behind the scenes, or delivering
            exceptional care in the community, there is a place for you here.
          </p>
        </div>
      </section>

      {/* Roles */}
      <section className="bg-[#f2f4f7] py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto space-y-8 sm:space-y-10">
          {roles.map((role) => (
            <article
              key={role.title}
              className="bg-white/90 border border-[#eddac9] rounded-[16px] sm:rounded-[24px] shadow-[0_25px_65px_rgba(16,24,40,0.08)] p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6"
            >
              <div>
                <h2
                  className="text-2xl sm:text-3xl font-light text-[#0d0d0d]"
                  style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
                >
                  {role.title}
                </h2>
                <p className="mt-3 sm:mt-4 text-base sm:text-lg">{role.intro}</p>
              </div>
              <div className="grid gap-6 sm:gap-8 md:grid-cols-2">
                <div>
                  <h3 className="text-base tracking-[0.3em] uppercase text-[#c46422] mb-3">
                    Key responsibilities include
                  </h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    {role.responsibilities.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-base tracking-[0.3em] uppercase text-[#c46422] mb-3">
                    What we're looking for
                  </h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm md:text-base">
                    {role.requirements.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              {role.closing && <p className="text-base font-medium text-[#2c2c2c]">{role.closing}</p>}
            </article>
          ))}
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            Why Work With Testa Care?
          </h2>
          <ul className="text-left list-disc pl-6 space-y-3 text-lg text-[#444]">
            <li>Competitive pay and benefits</li>
            <li>Supportive and friendly working environment</li>
            <li>High-quality training and professional development</li>
            <li>Opportunities for career progression</li>
            <li>Flexible working patterns</li>
            <li>A chance to make a real difference every day</li>
          </ul>
        </div>
        <div className="max-w-4xl mx-auto mt-10">
          <Link
            to="/our-benefits"
            className="relative block h-80 w-full rounded-[30px] overflow-hidden shadow-xl group"
            style={{
              backgroundImage: "url('/assets/images/tc-handshake.jpeg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/35 transition duration-300" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="border-2 border-white bg-white/90 px-8 py-3 text-base font-medium tracking-wide text-[#2c2c2c] shadow-md">
                Our Benefits
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#dde3ec] py-10 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center space-y-3 sm:space-y-4">
          <h3 className="text-xl sm:text-2xl font-light text-[#0d0d0d]">Apply Today</h3>
          <p className="text-sm sm:text-base">
            If you're passionate about delivering outstanding care, we would love to hear from you.
          </p>
          <div className="space-y-2 text-base sm:text-lg font-semibold text-[#2c2c2c]">
            <p>
              📩 Send your CV to:{' '}
              <a href="mailto:info@testacare.co.uk" className="underline hover:text-[#8a3c11]">
                info@testacare.co.uk
              </a>
            </p>
            <p>📞 Call us: +44 (0)7767 054238</p>
            <p>
              🌐 Visit:{' '}
              <a href="https://www.testacare.co.uk" className="underline hover:text-[#8a3c11]" target="_blank" rel="noopener noreferrer">
                www.testacare.co.uk
              </a>
            </p>
          </div>
        </div>
      </section>

      <FooterLinks />
      </div>
    </div>
  );
};

export default JoinOurTeam;
